import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useAppContext } from '../../../../redux/slices/appContext'
import SydInput from '../../../commonDesign/SydInput'
import useDebouncedValue from '../../../../hooks/useDebounceValue'
import Skeleton from '../../../atoms/Skeleton'
import { ICON_NAMES } from '../../../../constants'
import { useGroupSearch } from '../../../../api/groups'
import SydLabel from '../../../commonDesign/SydLabel'
import RoundedRectangleNode from '../Canvas/nodes/RoundedRectangleNode'
import { useEntityGraphContext } from '../Wrapper/EntityGraphProvider'
import { getRandomId } from './utils'

const getCategoryData = (accountCategory) => ({
  header: accountCategory.longName,
  description: accountCategory.description,
  nodeProps: {
    size: 'sm'
  },
  entityProps: {
    levelId: accountCategory.groupId,
    levelTypeId: 203,
    type: 'accountCategory'
  },
  displayOptions: {
    header: 'entityProps.label',
    description: accountCategory?.description?.length ? 'entityProps.description' : 'none',
    backgroundColor: 'entityProps.color',
    borderColor: 'entityProps.color'
  }
})

export const AccountCategoryList = ({
  actionType = 'creation',
  onSelectItem: _onSelectItem,
  selectedValue
}) => {
  const { clientId } = useAppContext()
  const { dispatch } = useEntityGraphContext()

  const [searchTerm, setSearchTerm] = useState('')

  const addLinkedDataToContext = useCallback((accountCategory) => {
    dispatch({
      type: 'ADD_LINKED_ENTITY_DATA',
      payload: {
        levelTypeId: 203,
        levelId: accountCategory.groupId,
        value: {
          label: accountCategory.longName,
          description: accountCategory.description,
          color: accountCategory.colorField,
          ...accountCategory
        }
      }
    })
  }, [dispatch])

  const onSelectItem = useCallback((accountCategory) => {
    addLinkedDataToContext(accountCategory)
    _onSelectItem?.(getCategoryData(accountCategory))
  }, [addLinkedDataToContext, _onSelectItem])

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300)
  const accountCategoryQuery = useMemo(() => ({
    forClientId: clientId,
    filters: {
      levelTypeId: [{ op: 'eq', value: 203 }],
      ...(selectedValue && {
        groupId: [{ op: 'eq', value: selectedValue.levelId }]
      })
    },
    take: 10,
    ...(debouncedSearchTerm?.length && {
      textSearch: {
        shortName: [{ op: 'contains', value: debouncedSearchTerm }],
        longName: [{ op: 'contains', value: debouncedSearchTerm }],
        description: [{ op: 'contains', value: debouncedSearchTerm }]
      }
    })
  }), [clientId, debouncedSearchTerm, selectedValue])
  const { data, isLoading } = useGroupSearch(accountCategoryQuery)

  const onDragStart = useCallback((event, accountCategory) => {
    if (actionType === 'linking') {
      return
    }
    event.dataTransfer.setData('application/reactflow', JSON.stringify({
      nodeType: 'roundedRectangle',
      id: `accountCategory_${accountCategory.groupId}_${getRandomId()}`,
      data: getCategoryData(accountCategory)
    }))
    event.dataTransfer.effectAllowed = 'move'
    addLinkedDataToContext(accountCategory)
  }, [actionType, addLinkedDataToContext])

  if (selectedValue) {
    return (
      <>
        {isLoading && (
          <Skeleton width='100%' height='38px' />
        )}
        {data?.data?.map((accountCategory) => (
          <RoundedRectangleNode
            key={accountCategory.groupId}
            width='100%'
            height='auto'
            data={{
              ...getCategoryData(accountCategory),
              nodeProps: {
                size: 'md',
                handles: [],
                targets: []
              }
            }}
          />
        ))}
      </>
    )
  }

  return (
    <>
      <div>
        <SydInput
          size='sm'
          iconName={ICON_NAMES.search}
          placeholder='Search...'
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          className='mb-2'
        />
      </div>
      <div className='flex flex-col gap-sm'>
        <SydLabel label={actionType === 'linking' ? 'Click a category to link it' : 'Drag category into the chart'} />
        {isLoading && Array.from({ length: 10 }).map((_, index) => (
          <Skeleton width='100%' height='38px' key={index} />
        ))}
        {data?.data?.map((accountCategory) => (
          <div
            draggable={actionType === 'creation'}
            key={accountCategory.groupId}
            className='relative'
            style={{
              cursor: actionType === 'creation' ? 'grab' : 'pointer'
            }}
            onDragStart={(e) => onDragStart(e, accountCategory)}
            onClick={() => {
              if (actionType === 'linking' && onSelectItem) {
                onSelectItem(accountCategory)
              }
            }}
          >
            <RoundedRectangleNode
              width='100%' height='auto' data={{
                ...getCategoryData(accountCategory),
                nodeProps: {
                  size: 'md',
                  handles: [],
                  targets: []
                }
              }}
            />
          </div>
        ))}
      </div>
    </>
  )
}

AccountCategoryList.propTypes = {
  actionType: PropTypes.oneOf(['creation', 'linking']),
  onSelectItem: PropTypes.func,
  selectedValue: PropTypes.object
}
